import React, {HTMLAttributes, RefAttributes, useCallback, useEffect, useRef, useState} from "react";
import {IonDatetime, IonInput} from "@ionic/react";
import {dateFormat} from "../../../../ts/common/DateFormat";
import {StyleReactProps} from "@ionic/react/dist/types/components/react-component-lib/interfaces";
import IntrinsicAttributes = React.JSX.IntrinsicAttributes;
import eventBus from "../../../../ts/event/EventBus";
import {
    DateFormItem,
    IDateFormItemProps,
    IFormItemElement,
} from "../model/FormModel";
import {dateToLocalISOString, requireDate} from "../../../../ts/common/SiteHelper";
import AppButton from "../../../element/button/AppButton";
import moment from "moment/moment";

interface IProps extends IFormItemElement
{
    initialDate?:Date
    formItemProps:IDateFormItemProps;
    minDate?:Date|string|number;
    maxDate?:Date|string|number;
    dateDisplayFormat?:string;
    inputElementProps?:IntrinsicAttributes & React.ComponentProps<typeof IonInput> & Omit<HTMLAttributes<HTMLIonInputElement>, "style"> & StyleReactProps & RefAttributes<typeof IonInput>
    dateElementProps?: IntrinsicAttributes & React.ComponentProps<typeof IonDatetime> & Omit<HTMLAttributes<HTMLIonDatetimeElement>, "style"> & StyleReactProps & RefAttributes<typeof IonDatetime>
}

const DateTimeFIE = (props: IProps) =>
{
    let {
        form,
        formItemProps,
        label,
        initialDate,
        onItemChange,
        inputElementProps,
        dateElementProps,
        dateDisplayFormat,
        instructions
    } = props;

    const formItem = form.requireFormItem(formItemProps, "date", true) as DateFormItem;

    label = label ?? formItem.name;

    const [showDateSelect, setShowDateSelect] = useState<boolean>(false);
    const [lastSelectedValue, setLastSelectedValue] = useState<Date|null>(formItem.value);
    const dateTimeRef = useRef<null | HTMLDivElement>(null);

    let dateDisplayValue:string = formItem.isoStringDate();
    dateDisplayValue = dateDisplayValue ? dateDisplayValue : dateToLocalISOString(initialDate ?? (formItem.getCastValue() ?? new Date()));

    let min:string|undefined = undefined;
    let max:string|undefined = undefined;
    if(props.minDate !== undefined){
        const minDate = requireDate(props.minDate);
        min = minDate ? dateToLocalISOString(minDate) : undefined
    }

    if(props.maxDate !== undefined){
        const maxDate = requireDate(props.maxDate);
        max = maxDate ? dateToLocalISOString(maxDate) : undefined
    }

    const formattedValue =
        formItem.getCastValue() ? (
            dateDisplayFormat ?
                moment(formItem.getCastValue()).format(dateDisplayFormat) :
                dateFormat(formItem.getCastValue(), {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                }
                )
            ) :
            ""

    const onBaseClick= useCallback((event: CustomEvent) =>
    {
        const htmlElement:HTMLElement|null = event.detail.htmlElement ?? null;
        const thisElementDateTime:HTMLElement|null = htmlElement?.closest(".element-date-time") ?? null;
        const ionPopover:HTMLElement|null = htmlElement?.closest("ion-popover") ?? null;
        if(!showDateSelect && (!thisElementDateTime || (thisElementDateTime !== dateTimeRef.current)) && !ionPopover){
            showHideDateSelect(false);
        }
    }, []);

    const removeBaseListener= () => {
        eventBus.remove("site.base-click", onBaseClick);
    }

    const addBaseListener= () => {
        removeBaseListener();
        eventBus.on("site.base-click", onBaseClick);
    }

    useEffect(() =>
    {


        return () =>
        {
            removeBaseListener();
        };
    }, []);

    const showHideDateSelect= (doShow:boolean) =>
    {
        setShowDateSelect(doShow);
        if(doShow){
            addBaseListener();
        } else {
            setLastSelectedValue(formItem.value)
            removeBaseListener();
        }
    }

    const onInputClick = (event:any) => {
        showHideDateSelect(!showDateSelect);
        return false;
    }

    const applyDateValue = (value:Date|string|number|null) => {
        if(!formItem.setValue(value)){
            return;
        }

        if(onItemChange){
            onItemChange(formItem.itemID);
            form.refreshState();
        } else if(onItemChange !== null){
            form.onFormItemChange(formItem.itemID);
        }
    }

    const onDateChange = (event:CustomEvent) => {
        const value = event.detail.value ?? null
        applyDateValue(value);
    }

    const onDateCancel = () => {
        applyDateValue(lastSelectedValue)
        showHideDateSelect(false);
    }

    const onDateClear = () => {
        applyDateValue(null)
        showHideDateSelect(false);
    }

    const onDateOK = () => {
        showHideDateSelect(false);
    }

    return <fieldset
        className={form.isFormItemHidden(formItem) ? "d-none" : ""}
    >
        <div
            className="row w-100 element-date-time"
            ref={dateTimeRef}
        >

            <div className="col-12">
                <div className="display-date" onClick={onInputClick}>
                    <IonInput
                        {...inputElementProps}
                        label={label}
                        labelPlacement={inputElementProps?.labelPlacement ?? "stacked"}
                        className={'input-element ion-touched ' + (formItem.errorText ? 'ion-invalid ' : '') + (inputElementProps?.className ?? "") + (formItem.required ? " required" : "")}
                        data-error-for-form-item-id={formItem.itemID}
                        data-form-item-id={formItem.itemID}
                        errorText={formItem.errorText}
                        placeholder={inputElementProps?.placeholder ?? "Select date"}
                        value={formattedValue}
                    />
                </div>
            </div>
            {showDateSelect && <>

                <div className="col-12">
                    <IonDatetime
                        {...dateElementProps}
                        value={dateDisplayValue}
                        min={min}
                        max={max}
                        minuteValues={dateElementProps?.minuteValues ?? "0,5,10,15,20,25,30,35,40,45,50,55"}
                        onIonChange={onDateChange}
                        showDefaultButtons={dateElementProps?.showDefaultButtons ?? false}
                        className={"float-end mt-3 date-time-picker " + (dateElementProps?.className ?? "")}
                    />
                </div>

                {!dateElementProps?.showDefaultButtons  && <>
                    <div className="col-12">
                        <div className="float-start">

                            <AppButton
                                disabled={!formItem.canReset()}
                                onClick={onDateCancel}
                                className="btn btn-secondary"
                            >
                                Cancel
                            </AppButton>
                        </div>
                        {!formItem.required  && <>
                            <AppButton
                                disabled={!formItem.value}
                                onClick={onDateClear}
                                className="btn btn-secondary"
                            >
                                Clear
                            </AppButton>
                        </>}
                        <div className="float-end">

                            <AppButton
                                onClick={onDateOK}
                                className="btn btn-primary"
                            >
                                OK
                            </AppButton>
                        </div>
                    </div>
                </>}
            </>
            }
            {!!instructions && (
                <div className="col-12">
                    <span className="instructions">{instructions}</span>
                </div>
            )}
        </div>

    </fieldset>
}

export default DateTimeFIE;